import type { ConferenceAiForGoodItem } from '../types';

type AiForGoodTextField = 'productName' | 'productDesc';
type AiForGoodLocale = 'zh' | 'en';

const AI_FOR_GOOD_TEXT_FIELDS: Record<
  AiForGoodTextField,
  { zh: keyof ConferenceAiForGoodItem; en: keyof ConferenceAiForGoodItem }
> = {
  productName: {
    zh: 'productNameCN',
    en: 'productNameEN',
  },
  productDesc: {
    zh: 'productDescCN',
    en: 'productDescEN',
  },
};

export const getAiForGoodCardText = (
  item: ConferenceAiForGoodItem,
  field: AiForGoodTextField,
  locale: AiForGoodLocale
) => {
  const keys = AI_FOR_GOOD_TEXT_FIELDS[field];
  const primary = locale === 'zh' ? keys.zh : keys.en;
  const fallback = locale === 'zh' ? keys.en : keys.zh;
  const primaryValue = `${item[primary] || ''}`.trim();
  if (primaryValue) {
    return primaryValue;
  }
  return `${item[fallback] || ''}`.trim();
};

export const getAiForGoodLogo = (item: ConferenceAiForGoodItem) => {
  // 兼容联调环境仍返回旧字段名的情况，避免后台已有 logo 数据时前台退回字母占位。
  const rawLogo = [
    item.logo,
    (item as ConferenceAiForGoodItem & { Logo?: string }).Logo,
    (item as ConferenceAiForGoodItem & { logoURL?: string }).logoURL,
    (item as ConferenceAiForGoodItem & { LogoURL?: string }).LogoURL,
  ].find((value) => `${value || ''}`.trim());

  return `${rawLogo || ''}`.trim();
};

const getAiForGoodLinkValue = (
  item: ConferenceAiForGoodItem,
  keys: string[]
) => {
  const value = keys.find((key) => `${(item as Record<string, unknown>)[key] || ''}`.trim());
  return `${(value && (item as Record<string, unknown>)[value]) || ''}`.trim();
};

export const hasAiForGoodWechatLink = (item: ConferenceAiForGoodItem) =>
  Boolean(getAiForGoodLinkValue(item, ['wxLinkURL', 'WxLinkURL']));

export const isValidAiForGoodLink = (value?: string) => {
  const trimmedValue = `${value || ''}`.trim();
  if (!trimmedValue) {
    return false;
  }
  try {
    const parsedURL = new URL(trimmedValue);
    return parsedURL.protocol === 'http:' || parsedURL.protocol === 'https:';
  } catch {
    return false;
  }
};

export const isWechatBrowser = (userAgent = '') => {
  const normalizedUserAgent = userAgent.toLowerCase();
  return normalizedUserAgent.includes('micromessenger') && !normalizedUserAgent.includes('wxwork');
};

export const resolveAiForGoodLink = (
  item: ConferenceAiForGoodItem,
  {
    isWechat,
    isMobile,
  }: {
    isWechat: boolean;
    isMobile: boolean;
  }
) => {
  const pcLink = getAiForGoodLinkValue(item, ['pcLinkURL', 'link', 'PcLinkURL', 'LinkURL']);
  const mobileLink = getAiForGoodLinkValue(item, ['mobileLinkURL', 'MobileLinkURL']);
  const wxLink = getAiForGoodLinkValue(item, ['wxLinkURL', 'WxLinkURL']);

  // 点击优先级：微信浏览器优先走微信端外链，其余情况按当前展示端决定，并在移动端缺省时回退 PC 外链。
  // 微信端链接可能是 weixin:// 等自定义 Scheme，只要存在即可使用。
  if (isWechat && wxLink) {
    return wxLink;
  }
  if (isMobile && isValidAiForGoodLink(mobileLink)) {
    return mobileLink;
  }
  return pcLink;
};

export const splitAiForGoodRows = (list: ConferenceAiForGoodItem[]) => {
  if (list.length <= 1) {
    return [list, []] as [ConferenceAiForGoodItem[], ConferenceAiForGoodItem[]];
  }
  const firstRowCount = Math.ceil(list.length / 2);
  return [
    list.slice(0, firstRowCount),
    list.slice(firstRowCount),
  ] as [ConferenceAiForGoodItem[], ConferenceAiForGoodItem[]];
};

const expandAiForGoodRow = (
  row: ConferenceAiForGoodItem[],
  minCount: number
) => {
  if (row.length === 0) {
    return [];
  }
  let expandedRow = [...row];
  while (expandedRow.length <= minCount) {
    expandedRow = expandedRow.concat(row);
  }
  return expandedRow;
};

export const buildAiForGoodMarqueeRows = (
  list: ConferenceAiForGoodItem[],
  minCount = 12
) => {
  const [firstRow, secondRow] = splitAiForGoodRows(list);
  return [
    expandAiForGoodRow(firstRow, minCount),
    expandAiForGoodRow(secondRow, minCount),
  ] as [ConferenceAiForGoodItem[], ConferenceAiForGoodItem[]];
};

export const getMobileAiForGoodMarqueeRows = (
  list: ConferenceAiForGoodItem[],
  minCount = 4
) => {
  const [firstRow, secondRow] = splitAiForGoodRows(list);
  return [
    expandAiForGoodRow(firstRow, minCount),
    expandAiForGoodRow(secondRow, minCount),
  ] as [ConferenceAiForGoodItem[], ConferenceAiForGoodItem[]];
};
